import React from "react";
import { Box, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import {
  BannerCardsStyle,
  PreviewBanner,
} from "../../../../service/BuyerConfiguration/BuyerAppDashboard/bannerCardsService";
import { BoxArt, CHIMNEY, ENGINE_LENGTH, ENGINE_TOP, EngineArt, Wheel, boxPath, useSvgId } from "./bannerArt";

export const DASH_GREEN = "#00B14F";
export const PREVIEW_WIDTH = 360;
const SIDE = 12;
const SHADOW = "drop-shadow(0 3px 6px rgba(0,0,0,0.28))";

/** True while the viewer asks for less motion. */
export const useReducedMotion = () => {
  const query = "(prefers-reduced-motion: reduce)";
  const [reduced, setReduced] = React.useState(
    () => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia(query).matches,
  );
  React.useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia(query);
    const on = () => setReduced(mq.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return reduced;
};

/** Seconds since mount (or since `resetKey` changed), ticking every frame while `running`. */
const useClock = (running: boolean, resetKey: unknown) => {
  const [t, setT] = React.useState(0);
  React.useEffect(() => {
    setT(0);
    if (!running) return;
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      setT((now - start) / 1000);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [running, resetKey]);
  return t;
};

/* ---------------------------------- row ---------------------------------- */

const StandaloneBox: React.FC<{ banner: PreviewBanner; style: BannerCardsStyle; w: number; h: number }> = ({
  banner,
  style,
  w,
  h,
}) => (
  <svg
    width={w}
    height={h}
    style={{ display: "block", flexShrink: 0, overflow: "visible", filter: style.box.shadow ? SHADOW : undefined }}
    role="img"
    aria-label={banner.title || "Banner"}
  >
    <BoxArt banner={banner} style={style} w={w} h={h} />
  </svg>
);

const RowView: React.FC<{ banners: PreviewBanner[]; style: BannerCardsStyle }> = ({ banners, style }) => {
  const items = banners.slice(0, 3);
  const inner = PREVIEW_WIDTH - SIDE * 2;
  const w = (inner - style.box.gap * (items.length - 1)) / Math.max(1, items.length);
  return (
    <Box sx={{ display: "flex", gap: `${style.box.gap}px`, px: `${SIDE}px` }}>
      {items.map((b) => (
        <StandaloneBox key={b.id} banner={b} style={style} w={w} h={style.box.height} />
      ))}
    </Box>
  );
};

/* -------------------------------- carousel -------------------------------- */

const CarouselView: React.FC<{ banners: PreviewBanner[]; style: BannerCardsStyle; reduced: boolean }> = ({
  banners,
  style,
  reduced,
}) => {
  const n = banners.length;
  const h = style.box.height;
  const w = Math.round(h * style.box.widthRatio);
  const step = w + style.box.gap;
  const [index, setIndex] = React.useState(0);
  const [instant, setInstant] = React.useState(false);

  React.useEffect(() => {
    setIndex(0);
  }, [n, step]);

  React.useEffect(() => {
    if (n < 2 || reduced) return;
    const id = window.setInterval(() => {
      setInstant(false);
      setIndex((i) => i + 1);
    }, style.carousel.intervalSec * 1000);
    return () => window.clearInterval(id);
  }, [n, style.carousel.intervalSec, reduced]);

  // The strip holds the list twice; after sliding onto the copy, jump back without animating.
  React.useEffect(() => {
    if (index < n || n < 1) return;
    const settle = () => {
      setInstant(true);
      setIndex((i) => i - n);
    };
    if (reduced) {
      settle();
      return;
    }
    const id = window.setTimeout(settle, 460);
    return () => window.clearTimeout(id);
  }, [index, n, reduced]);

  const strip = [...banners, ...banners];
  return (
    <Box>
      <Box sx={{ overflow: "hidden", px: `${SIDE}px`, py: "6px", my: "-6px" }}>
        <Box
          sx={{
            display: "flex",
            gap: `${style.box.gap}px`,
            transform: `translateX(${-index * step}px)`,
            transition: instant || reduced ? "none" : "transform 450ms cubic-bezier(.2,.7,.2,1)",
          }}
        >
          {strip.map((b, i) => (
            <StandaloneBox key={`${b.id}-${i}`} banner={b} style={style} w={w} h={h} />
          ))}
        </Box>
      </Box>
      {n > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", gap: "5px", mt: 1 }}>
          {banners.map((b, i) => (
            <Box
              key={b.id}
              sx={{
                width: i === index % n ? 14 : 6,
                height: 6,
                borderRadius: 3,
                bgcolor: i === index % n ? "#FFFFFF" : "rgba(255,255,255,0.45)",
                transition: reduced ? "none" : "width 200ms",
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

/* ---------------------------------- train --------------------------------- */

const easeOut = (p: number) => 1 - (1 - p) * (1 - p);
const easeIn = (p: number) => p * p;

/**
 * Where the train is at time t. x is the left edge (screen px) when moving
 * left; `dist` is how far it has travelled on this trip (for the wheels).
 */
export const trainMotion = (
  t: number,
  o: {
    width: number;
    length: number;
    speed: number;
    motion: "continuous" | "stop";
    stopSec: number;
    gapSec: number;
    direction: "left" | "right";
  },
): { x: number; dist: number; moving: boolean } => {
  const { width, length, speed, gapSec } = o;
  const centre = (width - length) / 2;
  let x: number;
  let dist: number;
  let moving: boolean;
  if (o.motion === "continuous") {
    const run = (width + length) / speed;
    const tt = t % (run + gapSec);
    dist = speed * Math.min(tt, run);
    moving = tt < run;
    x = moving ? width - dist : -length;
  } else {
    const d1 = width - centre;
    const d2 = centre + length;
    const t1 = (1.5 * d1) / speed;
    const t3 = (1.5 * d2) / speed;
    const tt = t % (t1 + o.stopSec + t3 + gapSec);
    if (tt < t1) {
      dist = d1 * easeOut(tt / t1);
      x = width - dist;
      moving = true;
    } else if (tt < t1 + o.stopSec) {
      dist = d1;
      x = centre;
      moving = false;
    } else if (tt < t1 + o.stopSec + t3) {
      const p = easeIn((tt - t1 - o.stopSec) / t3);
      dist = d1 + d2 * p;
      x = centre - d2 * p;
      moving = p > 0;
    } else {
      dist = d1 + d2;
      x = -length;
      moving = false;
    }
  }
  if (o.direction === "right") x = width - x - length;
  return { x, dist, moving };
};

const isLottie = (url: string) => /\.json(\?|#|$)/i.test(url) || /lottie/i.test(url);

const TrainView: React.FC<{
  banners: PreviewBanner[];
  style: BannerCardsStyle;
  reduced: boolean;
  restartKey: number;
}> = ({ banners, style, reduced, restartKey }) => {
  const id = useSvgId("tr");
  const { box, train } = style;
  const items = banners.slice(0, 3);
  const n = items.length;
  const left = train.direction === "left";

  // Geometry in train units (scaled to fit below).
  const boxH = box.height;
  const boxW = Math.round(box.height * box.widthRatio);
  const pad = 5;
  const coachW = boxW + pad * 2;
  const coachH = boxH + pad + 12;
  const k = coachH / 54; // engine units -> px
  const wr = 7 * k;
  const bodyBottom = -wr * 1.2;
  const engineW = ENGINE_LENGTH * k;
  const coupling = 6 * k;
  const length = engineW + n * (coupling + coachW);
  const scale = Math.min(1, (PREVIEW_WIDTH - 16) / length);
  const topUnits = Math.max(coachH - bodyBottom, ENGINE_TOP * k) + (train.smoke ? 14 * k : 4);
  const height = Math.ceil(topUnits * scale) + 14;
  const ground = height - 9;

  const t = useClock(!reduced, `${restartKey}`);
  const m = trainMotion(t, {
    width: PREVIEW_WIDTH,
    length: length * scale,
    speed: train.speed,
    motion: train.motion,
    stopSec: train.stopSec,
    gapSec: train.gapSec,
    direction: train.direction,
  });
  // Reduced motion: stand still at the station.
  const x = reduced ? (PREVIEW_WIDTH - length * scale) / 2 : m.x;
  const moving = !reduced && m.moving;
  const distUnits = (reduced ? 0 : m.dist) / scale;
  const turn = ((distUnits / wr) * 180) / Math.PI; // degrees, positive = clockwise
  const coachAngle = left ? -turn : turn;
  const bob = (i: number) => (moving && train.bounce ? -Math.abs(Math.sin(distUnits / 18 + i * 1.3)) * 1.6 : 0);

  // Lay the train out facing left, then mirror positions when it runs right.
  const place = (x0: number, w: number) => (left ? x0 : length - x0 - w);
  const engineX = place(0, engineW);
  const coaches = items.map((b, i) => ({ b, x: place(engineW + coupling + i * (coachW + coupling), coachW) }));
  const couplings = items.map((_, i) => place(engineW + i * (coachW + coupling), coupling));

  const chimney = train.engine.artUrl ? null : CHIMNEY[train.engine.preset];
  const puffs =
    train.smoke && chimney && moving
      ? Array.from({ length: 6 }, (_, i) => {
          const p = (t * 1.3 + i / 6) % 1;
          const back = left ? 1 : -1;
          const cxu = left ? chimney[0] : ENGINE_LENGTH - chimney[0];
          return {
            cx: engineX + (cxu + back * p * 26) * k,
            cy: (chimney[1] - p * 12) * k + bob(-1),
            r: (2.5 + p * 6) * k,
            o: (1 - p) * 0.75,
          };
        })
      : [];

  const art = train.engine.artUrl;
  const flip = left ? "" : ` translate(${engineW},0) scale(-1,1)`;

  return (
    <svg width={PREVIEW_WIDTH} height={height} style={{ display: "block" }} role="img" aria-label="Banner train preview">
      <defs>
        <linearGradient id={`${id}g`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={train.coach.color} />
          <stop offset="1" stopColor={train.coach.color2} />
        </linearGradient>
      </defs>

      {train.track.show && (
        <g>
          {Array.from({ length: Math.ceil(PREVIEW_WIDTH / 14) + 1 }, (_, i) => (
            <rect key={i} x={i * 14 + 2} y={ground + 2} width={7} height={4} rx={1} fill={train.track.color} opacity={0.55} />
          ))}
          <rect x={0} y={ground - 1} width={PREVIEW_WIDTH} height={2.5} rx={1} fill={train.track.color} />
        </g>
      )}

      <g transform={`translate(${x.toFixed(2)},${ground}) scale(${scale})`}>
        {puffs.map((p, i) => (
          <circle key={i} cx={p.cx} cy={p.cy} r={p.r} fill="#FFFFFF" opacity={p.o} />
        ))}

        {couplings.map((cx, i) => (
          <rect key={i} x={cx - 2} y={bodyBottom - 9 * k} width={coupling + 4} height={3 * k} rx={1} fill="#37474F" />
        ))}

        <g transform={`translate(${engineX},${bob(-1) * 0.7})`}>
          {art && isLottie(art) ? (
            <g>
              <rect
                x={1}
                y={-ENGINE_TOP * k * 0.8}
                width={engineW - 2}
                height={ENGINE_TOP * k * 0.8 - 1}
                rx={8}
                fill="rgba(255,255,255,0.2)"
                stroke="#FFFFFF"
                strokeDasharray="4 3"
              />
              <text
                x={engineW / 2}
                y={-ENGINE_TOP * k * 0.4}
                textAnchor="middle"
                fill="#FFFFFF"
                fontSize={11}
                fontWeight={700}
                fontFamily="Poppins, Inter, system-ui, sans-serif"
              >
                Lottie engine
              </text>
              <text
                x={engineW / 2}
                y={-ENGINE_TOP * k * 0.4 + 13}
                textAnchor="middle"
                fill="rgba(255,255,255,0.85)"
                fontSize={9}
                fontFamily="Poppins, Inter, system-ui, sans-serif"
              >
                plays in the app
              </text>
            </g>
          ) : art ? (
            <g transform={flip.trim() || undefined}>
              <image
                href={art}
                x={0}
                y={-ENGINE_TOP * k * 0.85}
                width={engineW}
                height={ENGINE_TOP * k * 0.85}
                preserveAspectRatio="xMidYMax meet"
              />
            </g>
          ) : (
            <g transform={`${flip} scale(${k})`.trim()}>
              <EngineArt
                preset={train.engine.preset}
                color={train.engine.color}
                accent={train.engine.accent}
                wheels={train.wheels}
                angle={-turn}
              />
            </g>
          )}
        </g>

        {coaches.map(({ b, x: cx }, i) => {
          const top = bodyBottom - coachH;
          return (
            <g key={`${b.id}-${i}`} transform={`translate(${cx},${bob(i)})`}>
              <rect x={-1} y={top - 4 * k} width={coachW + 2} height={6 * k} rx={3 * k} fill={train.coach.color2} />
              <rect x={0} y={top} width={coachW} height={coachH} rx={6} fill={`url(#${id}g)`} />
              <rect x={0} y={top + pad + boxH + 4} width={coachW} height={4} fill={train.coach.stripe} />
              <g transform={`translate(${pad - 2.5},${top + pad - 2.5})`}>
                <path d={boxPath(box, boxW + 5, boxH + 5)} fill={train.coach.frame} />
              </g>
              <g transform={`translate(${pad},${top + pad})`}>
                <BoxArt banner={b} style={style} w={boxW} h={boxH} />
              </g>
              {train.wheels.show ? (
                [0.2, 0.8].map((f) => (
                  <Wheel
                    key={f}
                    cx={coachW * f}
                    cy={-wr - bob(i)}
                    r={wr}
                    color={train.wheels.color}
                    hub={train.coach.stripe}
                    angle={coachAngle}
                  />
                ))
              ) : (
                <rect x={4} y={bodyBottom} width={coachW - 8} height={-bodyBottom - 2} rx={2} fill="#37474F" />
              )}
            </g>
          );
        })}
      </g>
    </svg>
  );
};

/* --------------------------------- preview -------------------------------- */

/** The top of the buyer home: green dash, search bar and the banner boxes. */
export const BannerCardsPreview: React.FC<{
  style: BannerCardsStyle;
  banners: PreviewBanner[];
  restartKey?: number;
}> = ({ style, banners, restartKey = 0 }) => {
  const reduced = useReducedMotion();
  return (
    <Box
      sx={{
        width: PREVIEW_WIDTH,
        maxWidth: "100%",
        bgcolor: DASH_GREEN,
        borderRadius: 4,
        pt: 1.5,
        pb: 2,
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          mx: `${SIDE}px`,
          mb: 1.5,
          height: 40,
          borderRadius: 20,
          bgcolor: "#FFFFFF",
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
        }}
      >
        <SearchIcon sx={{ fontSize: 20, color: DASH_GREEN }} />
        <Typography sx={{ fontSize: 13, color: "#8A8A8A", letterSpacing: 0.1 }}>
          Search for dishes, restaurants
        </Typography>
      </Box>
      {style.mode === "row" && <RowView banners={banners} style={style} />}
      {style.mode === "carousel" && <CarouselView banners={banners} style={style} reduced={reduced} />}
      {style.mode === "train" && <TrainView banners={banners} style={style} reduced={reduced} restartKey={restartKey} />}
      {reduced && style.mode !== "row" && (
        <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.85)", px: `${SIDE}px`, mt: 1 }}>
          Motion is paused because your device asks for reduced motion.
        </Typography>
      )}
    </Box>
  );
};

export default BannerCardsPreview;
